package xyz.malefic.kanman.client.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Background
import com.varabyte.kobweb.compose.css.BackgroundImage
import com.varabyte.kobweb.compose.css.BackgroundPosition
import com.varabyte.kobweb.compose.css.BackgroundRepeat
import com.varabyte.kobweb.compose.css.BackgroundSize
import com.varabyte.kobweb.compose.css.CSSPosition
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.css.TextWrap
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.draggable
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.onDragStart
import com.varabyte.kobweb.compose.ui.modifiers.overflowWrap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textWrap
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.ms.MsDelete
import com.varabyte.kobweb.silk.components.icons.ms.MsEdit
import com.varabyte.kobweb.silk.components.layout.Surface
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.div
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import xyz.malefic.kanman.client.styles.Color
import xyz.malefic.kanman.shared.data.model.StickyNoteModel
import xyz.malefic.kanman.shared.data.model.UserSummaryModel
import xyz.malefic.kanman.shared.util.isOverdue
import xyz.malefic.kanman.shared.util.toPrettyDateTime
import xyz.malefic.kutint.Kutint
import kotlin.uuid.Uuid

@Composable
fun StickyNote(
    color: Kutint<*>,
    stickyNote: StickyNoteModel,
    members: List<UserSummaryModel>,
    canEdit: Boolean = false,
    onEdit: () -> Unit = {},
    onDelete: () -> Unit = {},
    onAssignUser: (Uuid) -> Unit = {},
    onUnassignUser: (Uuid) -> Unit = {},
) {
    var showAssignPopup by remember { mutableStateOf(false) }
    val assignedUsers =
        stickyNote.assignedUsers.mapNotNull { assigned ->
            members.find { it.id == assigned.userId }
        }
    val dueDates = stickyNote.assignedUsers.associate { it.userId to it.due }
    val earliestDue = stickyNote.assignedUsers.mapNotNull { it.due }.minOrNull()
    val foldSize = 24.px

    var modifier =
        Modifier
            .fillMaxWidth()
            .minHeight(20.vw)
            .background(
                Background.of(
                    BackgroundImage.of(
                        linearGradient(315.deg) {
                            add(Colors.Transparent, foldSize / 2)
                            add(color, 0.percent)
                        },
                    ),
                ),
                Background.of(
                    BackgroundImage.of(
                        linearGradient(135.deg) {
                            add(color.shade(0.3f), 50.percent)
                            add(Colors.Transparent, 0.percent)
                        },
                    ),
                    BackgroundRepeat.NoRepeat,
                    BackgroundSize.of(foldSize, foldSize),
                    BackgroundPosition.of(CSSPosition(100.percent, 100.percent)),
                ),
            )

    if (canEdit) {
        modifier =
            modifier
                .draggable(true)
                .onDragStart { event ->
                    event.dataTransfer?.setData("text/plain", stickyNote.id.toString())
                }
    }

    Surface(modifier.padding(18.px).margin(topBottom = 12.px)) {
        Column(Modifier.fillMaxSize(), Arrangement.SpaceBetween) {
            Column(Modifier.fillMaxWidth()) {
                H3(
                    Modifier
                        .fillMaxWidth()
                        .color(Color.onTertiary)
                        .overflowWrap(OverflowWrap.BreakWord)
                        .toAttrs(),
                ) {
                    Text(stickyNote.title)
                }
                P(
                    Modifier
                        .fillMaxWidth()
                        .color(Color.onTertiary)
                        .whiteSpace(WhiteSpace.PreWrap)
                        .overflowWrap(OverflowWrap.BreakWord)
                        .textWrap(TextWrap.Wrap)
                        .toAttrs(),
                ) {
                    Text(stickyNote.content)
                }
                earliestDue?.let { due ->
                    P(
                        Modifier
                            .fillMaxWidth()
                            .color(if (due.isOverdue()) Color.error else Color.onTertiary)
                            .fontSize(12.px)
                            .margin(top = 8.px)
                            .toAttrs(),
                    ) {
                        Text("Due: ${due.toPrettyDateTime()}")
                    }
                }
            }

            Row(
                Modifier.fillMaxWidth().backgroundColor(Colors.Transparent).padding(0.px),
                Arrangement.End,
                Alignment.CenterVertically,
            ) {
                if (canEdit) {
                    UserAvatarRow(
                        color = Color.onTertiary,
                        assignedUsers = assignedUsers,
                        dueDates = dueDates,
                        canEdit = canEdit,
                        onAddClick = { showAssignPopup = true },
                        onUserClick = { onUnassignUser(it) },
                    )
                    Button(
                        { onEdit() },
                        Modifier.backgroundColor(Colors.Transparent).padding(0.px),
                    ) {
                        MsEdit(Modifier.color(Color.onTertiary))
                    }
                    Button(
                        { onDelete() },
                        Modifier.backgroundColor(Colors.Transparent).padding(0.px),
                    ) {
                        MsDelete(Modifier.color(Color.onTertiary))
                    }
                } else {
                    UserAvatarRow(
                        color = Color.onTertiary,
                        assignedUsers = assignedUsers,
                        dueDates = dueDates,
                    )
                }
            }
        }
    }

    if (showAssignPopup) {
        BoardMemberSearchOverlay(
            members = members,
            alreadyAssigned = stickyNote.assignedUsers.map { it.userId },
            onClose = { showAssignPopup = false },
            onSubmit = { onAssignUser(it) },
        )
    }
}
